import { useState, useCallback } from 'react'
import { synapseWorkerBridge } from '../../lib/SynapseWorkerBridge'
import type { TaskSubmitResponse } from '../../types/worker-messages'
import './TaskInput.css'

interface TaskInputProps {
  onSubmit?: (taskId: string) => void
}

export function TaskInput({ onSubmit }: TaskInputProps) {
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = useCallback(async () => {
    if (!description.trim() || isSubmitting) return

    setIsSubmitting(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await synapseWorkerBridge.send<TaskSubmitResponse>('task:submit', {
        description: description.trim(),
        priority: 'NORMAL'
      })

      if (response.type === 'error') {
        throw new Error((response.payload as any).message || 'Failed to submit task')
      }

      const taskId = response.payload.taskId

      setSuccess(`Task submitted: ${taskId.slice(0, 8)}`)
      setDescription('')
      onSubmit?.(taskId)

      // Clear success message after 3s
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit task')
    } finally {
      setIsSubmitting(false)
    }
  }, [description, isSubmitting, onSubmit])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit()
    }
  }, [handleSubmit])

  return (
    <div className="task-input-container">
      <div className="task-input-header">
        <span className="task-input-icon">+</span>
        <span className="task-input-title">New Task</span>
      </div>

      <div className="task-input-body">
        <textarea
          className="task-input-textarea"
          placeholder="Describe what you want to build..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSubmitting}
          rows={3}
        />

        <div className="task-input-footer">
          <span className="task-input-hint">Cmd+Enter to submit</span>

          {error && <span className="task-input-error">{error}</span>}
          {success && <span className="task-input-success">{success}</span>}

          <button
            className={`task-input-submit ${isSubmitting ? 'submitting' : ''}`}
            onClick={handleSubmit}
            disabled={!description.trim() || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Task'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TaskInput
